/**
 * Represents a fill in puzzle model or puzzle structure with words and grid locations.
 *
 * @class
 * @param {Object} finmodelData - The data object containing properties for the model.
 * @throws {Error} If finmodelData is invalid.
 *
 * @property {string} [type] - The type of the model or puzzle.
 * @property {number} [columncount] - The number of columns in the model.
 * @property {number} [rowcount] - The number of rows in the model.
 * @property {Array<Object>} [words] - The list of grid model word objects in the model.
 * @property {number} [startingword] - The starting word number or identifier.
 *
 * @method describe Returns a string summary of the model.
 * @method getAcrossWords Returns an array of words with orientation 'ACROSS'.
 * @method getDownWords Returns an array of words with orientation 'DOWN'.
 * @method getWordDictionary Returns an object mapping word values to their word objects. // Dictionary: { [wordvalue]: wordObject }
 * @method getStartingWordNumber Returns the starting word number as an integer.
 * @method getPuzzleType Returns the type of the puzzle or model.
 * @method getRowCount Returns the number of rows.
 * @method getColumnCount Returns the number of columns.
 * @method getWordCount Returns the total number of words.
 * @method getWords Returns the array of word objects.
 * @method getWordsByLength Returns a dictionary grouping word values by their length, sorted alphabetically.
 */
export class FinModel {
  constructor(finmodelData) {
    if (
      !finmodelData ||
      typeof finmodelData !== "object" ||
      Object.keys(finmodelData).length === 0
    ) {
      throw new Error("Invalid finmodel data");
    }
    // Copy all properties from finmodelData to this instance
    Object.assign(this, finmodelData);
  }
  describe() {
    const type = this.type || "Unknown";
    const columnCount = this.getColumnCount();
    const wordCount = Array.isArray(this.words) ? this.words.length : 0;
    return `T: ${type} S: ${columnCount} W: ${wordCount}`;
  }
  getAcrossWords() {
    if (!Array.isArray(this.words)) return [];
    return this.words.filter((word) => word.orientation === "ACROSS");
  }
  getDownWords() {
    if (!Array.isArray(this.words)) return [];
    return this.words.filter((word) => word.orientation === "DOWN");
  }
  getWordDictionary() {
    if (!Array.isArray(this.words)) return {};
    return this.words.reduce((dict, word) => {
      dict[word.wordvalue] = word;
      return dict;
    }, {});
  }
  /**
   * Returns the word object corresponding to the starting word number.
   * @returns {Object|null}
   */
  getStartingWord() {
    const startNum = this.getStartingWordNumber();
    if (!Array.isArray(this.words)) return null;
    return this.words[startNum];
  }
  getStartingWordNumber() {
    return parseInt(this.startingword, 10) - 1 || 0; // 0's based
  }
  getPuzzleType() {
    return this.type || "UNKNOWN";
  }
  getRowCount() {
    return this.rowcount || 0;
  }
  getColumnCount() {
    return this.columncount || 0;
  }
  getWordCount() {
    return Array.isArray(this.words) ? this.words.length : 0;
  }
  getWords() {
    return this.words || [];
  }
  /**
   * Returns a dictionary grouping word values by their length.
   * @returns {Object<string, Array<string>>}
   */
  // This method groups words by their length and sorts them alphabetically
  // into a dictionary where the keys are "Letters_X" (X being the length of the words).
  // Each key maps to an array of words of that length.
  // For example:
  // {
  //   "Letters_3": ["cat", "dog"],
  //   "Letters_4": ["fish", "frog"]
  // }
  getSortedWordsByLength() {
    if (!Array.isArray(this.words)) return {};
    const dict = {};
    this.words.forEach((word) => {
      if (typeof word.wordvalue === "string") {
        const len = word.wordvalue.length;
        const key = `Letters_${len}`;
        if (!dict[key]) dict[key] = [];
        dict[key].push(word.wordvalue);
      }
    });
    // Sort each list alphabetically
    Object.keys(dict).forEach((key) => {
      dict[key].sort((a, b) => a.localeCompare(b));
    });
    return dict;
  }
  /**
   * Get the puzzle type (ALPHA, NUMBER, ALPHANUMERIC, etc.)
   * @returns {string} - The puzzle type
   */
  getType() {
    return this.type || "ALPHA"; // Default to ALPHA if not specified
  }
}
